type ErrorRecord = Record<string, unknown>;

const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Não foi possível concluir a solicitação. Confira os dados informados e tente novamente.',
  401: 'Sua sessão expirou. Entre novamente para continuar.',
  403: 'Você não tem permissão para realizar esta ação.',
  404: 'O item solicitado não foi encontrado. Atualize a página e tente novamente.',
  408: 'A solicitação demorou demais para ser concluída. Tente novamente.',
  409: 'Esta operação não pôde ser concluída porque os dados foram alterados ou já existem.',
  422: 'Não foi possível concluir a solicitação. Verifique os dados informados.',
  429: 'Muitas solicitações foram feitas em pouco tempo. Aguarde um instante e tente novamente.',
};

export class GenericApiError extends Error {
  constructor(public readonly status: number) {
    super(`Generic API request failed with status ${status}`);
    this.name = 'GenericApiError';
  }
}

function asRecord(value: unknown): ErrorRecord | undefined {
  return typeof value === 'object' && value !== null
    ? (value as ErrorRecord)
    : undefined;
}

function getHttpStatus(error: unknown): number | undefined {
  const record = asRecord(error);
  const response = asRecord(record?.response);
  const status =
    record?.httpStatus ??
    record?.statusCode ??
    record?.status ??
    response?.status;
  return typeof status === 'number' ? status : undefined;
}

export function getGenericErrorMessage(
  error: unknown,
  fallback: string
): string {
  const record = asRecord(error);
  const status = getHttpStatus(error);

  if (status !== undefined) {
    if (HTTP_ERROR_MESSAGES[status]) {
      return HTTP_ERROR_MESSAGES[status];
    }
    if (status >= 400 && status < 500) {
      return 'Não foi possível concluir a solicitação. Confira os dados informados e tente novamente.';
    }
    if (status >= 500) {
      return 'O serviço está temporariamente indisponível. Tente novamente em alguns minutos.';
    }
  }

  const code = record?.code;
  if (
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT'
  ) {
    return 'Não foi possível conectar ao serviço. Verifique sua conexão e tente novamente.';
  }

  if (error instanceof GenericApiError) {
    return 'Ocorreu um problema ao processar sua solicitação. Tente novamente.';
  }

  return fallback;
}

const API_MESSAGE_STATUSES = new Set([400, 404, 409]);

// Erros de validação da própria API (400, 404, 409) trazem uma mensagem útil
// para o usuário, como os registros que impedem a exclusão.
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const status = getHttpStatus(error);
  const response = asRecord(asRecord(error)?.response);
  const message = asRecord(response?.data)?.message;

  if (
    status !== undefined &&
    API_MESSAGE_STATUSES.has(status) &&
    typeof message === 'string' &&
    message
  ) {
    return message;
  }

  return getGenericErrorMessage(error, fallback);
}

export function throwIfGenericApiError(response: unknown): void {
  const status = getHttpStatus(response);

  if (status !== undefined && status >= 400) {
    throw new GenericApiError(status);
  }
}
